import { ENDPOINT_LAB_ANALYSIS2, MeasureType } from '@/common/constants';
import { ContractStatusesEnum } from '@/common/constants/contract.statuses';
import { getValidationMessages } from '@/common/constants/validation-messages';
import { HttpApi } from '@/common/http';
import { Button } from '@/components/ui/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';
import { IAnalysis } from '../../models';
import { useLoadStore } from '../../store';
import { DialogAnalysis } from '../dialog/Dialog';
import { AnalysisData } from './AnalysisData';
import { AnalysisForm } from './AnalysisForm';

const messages = getValidationMessages();
export type FormComponentRef = {
  submit: () => void;
};

export const GeneralSchema = yup.object().shape({
  class: yup.number().required(messages.required),
  industrial_grade: yup.number().required(messages.required),
  humidity: yup.number().required(messages.required),
  pollution: yup.number().required(messages.required),
  analysis_crop_type_id: yup.string().required(messages.required),
  // fiber_type: yup.string().required(messages.required),
  // fiber_code: yup.string().required(messages.required),
  party_id: yup.string().required(messages.required),
  analysis_date: yup.string().notRequired(),
});

const httpApi = new HttpApi();

export const AnalysisWrapper: FC = () => {
  const navigate = useNavigate();
  const pathname = useLocation();
  const form = useForm<IAnalysis | any>({
    defaultValues: {},
    resolver: yupResolver(GeneralSchema),
  });

  const { load, fetchDataDetail, fetchLoadList } = useLoadStore();
  const formRef = useRef<FormComponentRef>(null);

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [actionActive, setActionActive] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { status = 'ALL', id } = useParams();

  const size = Number(searchParams.get('size')) || 20;
  const page = Number(searchParams.get('page')) || 1;
  const id_number = Number(searchParams.get('id_number')) || undefined;

  const isDeleted =
    load?.farmer_contract_status === ContractStatusesEnum.DELETED ||
    load?.farmer_contract_status === ContractStatusesEnum.CANCELED;

  const filters = useMemo(
    () => ({
      page,
      size,
      id_number,
    }),
    [page, size, id_number],
  );

  const payload = useMemo(
    () =>
      status === 'ALL'
        ? filters
        : {
            has_analysis: status === 'NOT_FILLED' ? false : true,
            ...filters,
          },
    [status, filters],
  );

  const onSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      await httpApi.put(`${ENDPOINT_LAB_ANALYSIS2}/${id}`, data);
      fetchDataDetail(id as string);
      fetchLoadList(payload);
      toast.success(t('success.saved'));
      setOpenDialog(false);
      setIsLoading(false);
      navigate(pathname, {
        replace: true,
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };
  const handleSaveClick = () => {
    formRef.current?.submit();
  };

  return (
    <FormProvider {...form}>
      <div className="pt-8 border-t">
        <h3 className="text-lg font-medium mb-4">{t('lab.facility')}</h3>

        {actionActive ? (
          <AnalysisForm
            ref={formRef}
            cancel={() => setActionActive(false)}
            onSubmit={onSubmit}
            setOpenDialog={setOpenDialog}
            openDialog={openDialog}
            harvest_type={load?.harvest_type}
            analysis={load?.analysis}
            crop_type_id={load?.crop_type_id}
            crop_generation_type={load?.crop_generation_type!}
          />
        ) : (
          <>
            <AnalysisData load={load} />
            {!load?.analysis?.analysis_date &&
              load?.current_status !== MeasureType.ENTRY && (
                <Button
                  variant="outline"
                  onClick={() => setActionActive(true)}
                  className="font-medium flex-h-center mt-5"
                  disabled={isDeleted}
                >
                  <span>{t('add.lab.facility')}</span>
                </Button>
              )}
          </>
        )}
        <DialogAnalysis
          formAnalysis={form}
          onSave={handleSaveClick}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          isLoading={isLoading}
        />
      </div>
    </FormProvider>
  );
};
